import React from "react";
import { Button } from "@/shared/components/ui/button";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { PeopleSelector } from "@/shared/components/PeopleSelector";
import type { PaymentTier } from "@/shared/utils/tierOrder";

const PAYWALL_VARIANTS: { tier: PaymentTier; label: string }[] = [
  { tier: "free", label: "Free" },
  { tier: "basic", label: "On Estate Setup" },
  { tier: "premium", label: "On Asset Search" },
];

const CustomPage: React.FC = () => {
  const [paywallTier, setPaywallTier] = React.useState<PaymentTier | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [singlePerson, setSinglePerson] = React.useState<number | null>(null);
  const [multiPeople, setMultiPeople] = React.useState<number[]>([]);

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Custom Components
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Application-specific components built for Elayne workflows.
        </p>
      </header>

      <div className="space-y-16">
        {/* PostLossPaywall */}
        <section id="postloss-paywall" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">PostLossPaywall</h2>
            <p className="mt-1 text-sm text-stone-500">Premium feature paywall dialog.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {PAYWALL_VARIANTS.map(({ tier, label }) => (
              <Button key={tier} onClick={() => setPaywallTier(tier)} variant="outline">
                {label}
              </Button>
            ))}
          </div>
          <PostLossPaywall
            isOpen={paywallTier !== null}
            onOpenChange={(open) => !open && setPaywallTier(null)}
            promptLocation="style_guide"
            currentTier={paywallTier}
          />
        </section>

        {/* ConfirmDeleteDialog */}
        <section id="confirm-delete-dialog" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">ConfirmDeleteDialog</h2>
            <p className="mt-1 text-sm text-stone-500">Destructive action confirmation dialog.</p>
          </div>
          <div>
            <Button onClick={() => setConfirmDeleteOpen(true)} variant="outline">
              Show ConfirmDeleteDialog
            </Button>
          </div>
          <ConfirmDeleteDialog
            isOpen={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={() => setConfirmDeleteOpen(false)}
          />
        </section>

        {/* PeopleSelector */}
        <section id="people-selector" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">PeopleSelector</h2>
            <p className="mt-1 text-sm text-stone-500">
              Picks one or more people from the estate contacts.
            </p>
          </div>
          <div className="grid max-w-3xl grid-cols-2 gap-6">
            <PeopleSelector
              label="Single"
              mode="single"
              value={singlePerson}
              onChange={setSinglePerson}
            />
            <PeopleSelector
              label="Multi"
              mode="multi"
              value={multiPeople}
              onChange={setMultiPeople}
            />
            <PeopleSelector
              label="Without search"
              mode="single"
              showSearch={false}
              value={singlePerson}
              onChange={setSinglePerson}
            />
            <PeopleSelector
              label="With error"
              mode="single"
              value={null}
              onChange={setSinglePerson}
              error="Choose a person"
            />
            <PeopleSelector
              label="No people"
              mode="single"
              filter={() => false}
              value={null}
              onChange={setSinglePerson}
            />
            <PeopleSelector
              label="Disabled"
              mode="single"
              disabled
              value={singlePerson}
              onChange={setSinglePerson}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default CustomPage;
